import * as React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Clapperboard,
  Command,
  Database,
  Coins,
  LayoutDashboard,
  LogOut,
  Moon,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Sun,
  Stethoscope,
  User,
  ImageIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MODULES, MODULE_GROUPS, MODULE_BADGES, isModuleEnabled } from '@/lib/modules';
import type { ModuleGroup } from '@/lib/modules';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useTheme } from '@/hooks/use-theme';
import { NotificationBell } from '@/components/notification-bell';

// 模块图标映射（模块注册表只存业务数据，图标留在布局层）
const MODULE_ICONS: Record<string, any> = {
  overview: LayoutDashboard,
  pulse: Activity,
  growth: BarChart3,
  project: Command,
  diagnosis: Stethoscope,
  monitor: Monitor,
  library: Database,
  persona: User,
  content: Clapperboard,
  assets: Database,
  templates: ImageIcon,
};

// 侧边栏菜单由模块注册表生成：新增页面在 modules.ts 加一行即可自动出现
const navGroups = MODULE_GROUPS.map((g: ModuleGroup) => ({
  label: g,
  items: MODULES.filter((m) => m.group === g).map((m) => ({
    to: m.path,
    name: m.label,
    icon: MODULE_ICONS[m.key] || LayoutDashboard,
    badge: m.badge,
    end: m.path === '/',
  })),
}));
// 系统组追加后台管理（不纳入模块注册表，始终仅管理员可见）
navGroups.find((g) => g.label === '系统')?.items.push({ to: '/admin', name: '后台管理', icon: Settings, badge: undefined, end: false });

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const modules = user?.permissions?.modules || {};

  // 菜单权限映射：path → 权限 key（全部模块受控，未设置 = 开）
  const modulePerms: Record<string, boolean> = {};
  for (const m of MODULES) {
    modulePerms[m.path] = isModuleEnabled(modules, m.key);
  }

  const visibleGroups = navGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        if (item.to === '/admin' && !isAdmin) return false;
        // 非管理员受模块可见性控制；管理员全显
        if (item.to in modulePerms && !isAdmin && !modulePerms[item.to]) return false;
        return true;
      }),
    }))
    .filter((g) => g.items.length > 0);

  const handleLogout = React.useCallback(async () => {
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  // ─── 侧边栏收缩（自动 + 手动） ───
  // 临界宽度 1024px（Tailwind lg 断点）：更窄（手机/平板竖屏）强制折叠以适配小屏，
  // 宽屏按用户手动偏好（localStorage 持久化）
  const [sidebarManual, setSidebarManual] = React.useState<'collapsed' | 'expanded' | null>(() => {
    try {
      const v = localStorage.getItem('milli_sidebar_manual');
      return v === 'collapsed' || v === 'expanded' ? v : null;
    } catch {
      return null;
    }
  });
  const [isNarrow, setIsNarrow] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // 窄屏临时展开/折叠（不持久化，刷新或进出窄屏后恢复默认折叠）
  const [narrowOverride, setNarrowOverride] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    setNarrowOverride(null);
  }, [isNarrow]);

  // 窄屏强制折叠（无视手动值）；宽屏按手动偏好
  const sidebarCollapsed = isNarrow ? (narrowOverride ?? true) : sidebarManual === 'collapsed';

  const toggleSidebar = React.useCallback(() => {
    if (isNarrow) {
      // 窄屏：仅本次会话临时切换，不写 localStorage
      setNarrowOverride((prev) => !(prev ?? true));
      return;
    }
    // 宽屏：持久化手动偏好
    const next: 'collapsed' | 'expanded' = sidebarManual === 'collapsed' ? 'expanded' : 'collapsed';
    try {
      localStorage.setItem('milli_sidebar_manual', next);
    } catch {
      /* 忽略存储失败 */
    }
    setSidebarManual(next);
  }, [isNarrow, sidebarManual]);

  return (
    <div className="min-h-screen bg-background">
      {/* ─── 顶部边栏（全宽贯通：Logo 在最左，用户区在右） ─── */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-sidebar-border bg-sidebar text-sidebar-foreground">
        {/* 品牌区 */}
        <div className="flex h-full items-center gap-3 px-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            MI
          </div>
          {!sidebarCollapsed && (
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold leading-none">米粒AI</div>
              <div className="mt-1 hidden truncate text-xs text-muted-foreground sm:block">经营与社媒一体化</div>
            </div>
          )}
        </div>

        {/* 用户区（通知/主题已下移侧边栏底部，此处保留积分/头像/退出） */}
        <div className="flex h-full items-center gap-1 pr-3">
          {user && (
            <>
              <button
                type="button"
                onClick={() => navigate('/credits')}
                title="查看积分消耗日志"
                className="mx-1 flex h-7 items-center gap-1.5 rounded-md bg-sidebar-accent px-2.5 text-xs font-medium transition-colors hover:bg-sidebar-accent/70"
              >
                <Coins className="h-3.5 w-3.5 text-amber-500" />
                <span className="hidden sm:inline">积分 </span>
                {(user.creditBalance ?? 0).toFixed(2)}
              </button>
              <div className="flex items-center gap-2.5 rounded-md px-2 py-1">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-sidebar-accent text-xs font-bold">
                  {user?.displayName?.[0] || '米'}
                </div>
                <div className="hidden min-w-0 md:block">
                  <div className="truncate text-sm font-medium leading-tight">{user?.displayName || '未登录'}</div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    {user?.role === 'admin' ? '管理员' : '用户'}
                  </div>
                </div>
              </div>
            </>
          )}
          <Button variant="ghost" size="icon" onClick={handleLogout} title="退出登录">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="flex min-h-screen">
        {/* ─── 侧边栏（只留导航；可收缩为仅图标窄条） ─── */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-30 flex flex-col border-r bg-sidebar pt-14 text-sidebar-foreground transition-[width] duration-200',
            sidebarCollapsed ? 'w-16' : 'w-[205px]'
          )}
        >
          <nav className={cn('flex-1 overflow-y-auto py-4', sidebarCollapsed ? 'px-2' : 'px-3')}>
            {visibleGroups.length === 0 && !sidebarCollapsed && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                暂无可用模块，请联系管理员开通
              </div>
            )}
            {visibleGroups.map((group) => (
              <div key={group.label} className={sidebarCollapsed ? 'mb-4' : 'mb-6'}>
                {!sidebarCollapsed && (
                  <div className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
                    {group.label}
                  </div>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      title={sidebarCollapsed ? item.name : undefined}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center rounded-md py-2 text-sm font-medium transition-colors',
                          sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-3',
                          isActive
                            ? 'bg-accent text-accent-foreground'
                            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                        )
                      }
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!sidebarCollapsed && (
                        <>
                          <span className="flex-1 truncate">{item.name}</span>
                          {item.badge && (
                            <span
                              className={`border px-1 text-[9px] font-bold leading-[14px] ${MODULE_BADGES[item.badge].className}`}
                            >
                              {MODULE_BADGES[item.badge].label}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* 底部：折叠按钮（左）+ 通知/主题（右），折叠态竖排居中 */}
          <div
            className={cn(
              'border-t',
              sidebarCollapsed ? 'flex flex-col items-center gap-1 py-2' : 'flex items-center gap-1 px-3 py-2'
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              title={sidebarCollapsed ? '展开侧边栏' : '收缩侧边栏'}
              className="h-8 w-8"
            >
              {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
            {!sidebarCollapsed && <div className="flex-1" />}
            <NotificationBell />
            <Button variant="ghost" size="icon" onClick={toggleTheme} title="切换主题">
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
          </div>
        </aside>

        {/* ─── 主内容区（pt-14 避开顶栏，pl 随侧边栏宽度联动） ─── */}
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col pt-14 transition-[padding-left] duration-200',
            sidebarCollapsed ? 'pl-16' : 'pl-[205px]'
          )}
        >
          <main className="flex-1 p-6 lg:p-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
